/**
 * B1ResourcesEditor.jsx - Sprint 2
 * 
 * Editor de recursos de aprendizaje (B1).
 * Gestiona: videos YouTube, PDFs, links externos, checklists.
 * 
 * @see PLAN_GROUPCOURSE_BUILDER.md
 */

import React, { useState } from 'react';
import { 
  Plus,
  Trash2,
  Edit2,
  ChevronUp,
  ChevronDown,
  Youtube,
  FileText,
  Link as LinkIcon,
  CheckSquare,
  X,
  Save,
  AlertCircle
} from 'lucide-react';

const B1ResourcesEditor = ({ resources = [], onChange }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [formData, setFormData] = useState({
    type: 'youtube',
    title: '',
    url: '',
    estimatedMinutes: 15,
    mandatory: false,
    description: ''
  });
  const [errors, setErrors] = useState({});

  const resourceTypes = [
    { value: 'youtube', label: 'Video YouTube', icon: Youtube, color: 'red' },
    { value: 'pdf', label: 'PDF / Documento', icon: FileText, color: 'blue' },
    { value: 'link', label: 'Link externo', icon: LinkIcon, color: 'green' },
    { value: 'checklist', label: 'Checklist', icon: CheckSquare, color: 'purple' }
  ];

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'El título es obligatorio';
    }
    
    if (!formData.url.trim() && formData.type !== 'checklist') {
      newErrors.url = 'La URL es obligatoria';
    }
    
    if (formData.type === 'youtube' && formData.url) {
      const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/;
      if (!youtubeRegex.test(formData.url)) {
        newErrors.url = 'URL de YouTube inválida';
      }
    }
    
    if (formData.estimatedMinutes < 1 || formData.estimatedMinutes > 300) {
      newErrors.estimatedMinutes = 'Duración debe estar entre 1 y 300 minutos';
    }
    
    return newErrors;
  };

  const handleAdd = () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    const newResources = [...resources, { ...formData, resourceId: Date.now().toString() }];
    onChange(newResources);
    resetForm();
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    setFormData({ ...resources[index] });
    setErrors({});
  };

  const handleUpdate = () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    const newResources = [...resources];
    newResources[editingIndex] = formData;
    onChange(newResources);
    resetForm();
  };

  const handleDelete = (index) => {
    if (confirm('¿Eliminar este recurso?')) {
      const newResources = resources.filter((_, i) => i !== index);
      onChange(newResources);
    }
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const newResources = [...resources];
    [newResources[index - 1], newResources[index]] = [newResources[index], newResources[index - 1]];
    onChange(newResources);
  };

  const handleMoveDown = (index) => {
    if (index === resources.length - 1) return;
    const newResources = [...resources];
    [newResources[index], newResources[index + 1]] = [newResources[index + 1], newResources[index]];
    onChange(newResources);
  };

  const resetForm = () => {
    setFormData({
      type: 'youtube',
      title: '',
      url: '',
      estimatedMinutes: 15,
      mandatory: false,
      description: ''
    });
    setIsAdding(false);
    setEditingIndex(null);
    setErrors({});
  };

  const getResourceIcon = (type) => {
    const resourceType = resourceTypes.find(rt => rt.value === type);
    if (!resourceType) return LinkIcon;
    return resourceType.icon;
  };

  const getResourceColor = (type) => {
    const resourceType = resourceTypes.find(rt => rt.value === type);
    return resourceType?.color || 'gray';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Recursos de Aprendizaje (B1)
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Videos, documentos y materiales para esta semana
          </p>
        </div>
        {!isAdding && editingIndex === null && (
          <button
            onClick={() => setIsAdding(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Agregar Recurso
          </button>
        )}
      </div>

      {/* Lista de recursos */}
      {resources.length > 0 && (
        <div className="space-y-3">
          {resources.map((resource, index) => {
            const Icon = getResourceIcon(resource.type);
            const color = getResourceColor(resource.type);
            
            return (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-lg p-4 flex items-start gap-4 hover:border-gray-300 transition-colors"
              >
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0}
                    title="Mover arriba"
                    className="text-gray-400 hover:text-gray-700 disabled:opacity-25 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleMoveDown(index)}
                    disabled={index === resources.length - 1}
                    title="Mover abajo"
                    className="text-gray-400 hover:text-gray-700 disabled:opacity-25 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>

                <div className={`p-3 bg-${color}-50 rounded-lg`}>
                  <Icon className={`w-6 h-6 text-${color}-600`} />
                </div>

                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900 flex items-center gap-2">
                        {resource.title}
                        {resource.mandatory && (
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
                            Obligatorio
                          </span>
                        )}
                      </h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {resource.description || 'Sin descripción'}
                      </p>
                      {resource.url && resource.type !== 'checklist' && (
                        <a
                          href={resource.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:underline mt-1 inline-block"
                        >
                          {resource.url}
                        </a>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                    <span>⏱️ {resource.estimatedMinutes} min</span>
                    <span className="capitalize">{resourceTypes.find(rt => rt.value === resource.type)?.label}</span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(index)}
                    className="text-gray-400 hover:text-blue-600 transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(index)}
                    className="text-gray-400 hover:text-red-600 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Formulario de agregar/editar */}
      {(isAdding || editingIndex !== null) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-gray-900">
              {editingIndex !== null ? 'Editar Recurso' : 'Nuevo Recurso'}
            </h4>
            <button
              onClick={resetForm}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            {/* Tipo de recurso */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Recurso
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {resourceTypes.map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.value}
                      onClick={() => setFormData({ ...formData, type: type.value, url: '' })}
                      className={`p-3 border-2 rounded-lg flex flex-col items-center gap-2 transition-all ${
                        formData.type === type.value
                          ? `border-${type.color}-500 bg-${type.color}-50`
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Icon className={`w-6 h-6 ${formData.type === type.value ? `text-${type.color}-600` : 'text-gray-400'}`} />
                      <span className="text-xs font-medium text-gray-700">{type.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Título */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Título del Recurso *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ej: Video introducción a la metodología"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {errors.title && (
                <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.title}
                </p>
              )}
            </div>

            {/* URL (no para checklist) */}
            {formData.type !== 'checklist' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL del Recurso *
                </label>
                <input
                  type="url"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder={
                    formData.type === 'youtube' 
                      ? 'https://youtube.com/watch?v=...' 
                      : 'https://...'
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {errors.url && (
                  <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.url}
                  </p>
                )}
              </div>
            )}

            {/* Descripción */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descripción
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descripción breve del recurso y su utilidad..."
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>

            {/* Duración estimada */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Duración Estimada (minutos)
              </label>
              <input
                type="number"
                value={formData.estimatedMinutes}
                onChange={(e) => setFormData({ ...formData, estimatedMinutes: parseInt(e.target.value) || 0 })}
                min="1"
                max="300"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {errors.estimatedMinutes && (
                <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.estimatedMinutes}
                </p>
              )}
            </div>

            {/* Obligatorio */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="mandatory"
                checked={formData.mandatory}
                onChange={(e) => setFormData({ ...formData, mandatory: e.target.checked })}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="mandatory" className="text-sm text-gray-700">
                Marcar como recurso obligatorio
              </label>
            </div>

            {/* Botones */}
            <div className="flex gap-3 pt-4 border-t border-blue-200">
              <button
                onClick={resetForm}
                className="px-4 py-2 text-gray-700 hover:bg-white rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={editingIndex !== null ? handleUpdate : handleAdd}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                <Save className="w-4 h-4" />
                {editingIndex !== null ? 'Actualizar' : 'Agregar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {resources.length === 0 && !isAdding && editingIndex === null && (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Sin recursos aún
          </h3>
          <p className="text-gray-600 mb-4">
            Agrega videos, PDFs, links y checklists para esta semana
          </p>
          <button
            onClick={() => setIsAdding(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg inline-flex items-center gap-2 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Agregar Primer Recurso
          </button>
        </div>
      )}
    </div>
  );
};

export default B1ResourcesEditor;
